import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Moon, Sun, LogOut, User, Menu, Smile } from 'lucide-react';
import { useAuth } from '../contexts/AuthProvider';
import { useTickets } from '../contexts/TicketsProvider';
import { cn } from '../lib/utils';
import StatusBar from './StatusBar';
import CommandPalette from './CommandPalette';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import TicketModal from './TicketModal';
import Celebration from './Celebration';
import TermyChat from './TermyChat';
import { TERMY_ENABLED } from '../lib/features';
import NotificationBell from './NotificationBell';
import UserAvatar from './UserAvatar';
import tickyMascot from '../assets/ticky-mascot.svg';

// Sidebar, in sections: day-to-day work, insight, and setup. Section titles
// read as terminal comments to match the prompt glyphs on the entries.
const NAV_SECTIONS = [
  { title: 'work', items: [
    { to: '/board', label: 'board' },
    { to: '/planning', label: 'planning' },
    { to: '/tickets', label: 'tickets' },
  ] },
  { title: 'insights', items: [
    { to: '/analytics', label: 'analytics' },
  ] },
  { title: 'setup', items: [
    { to: '/team', label: 'team', hint: 'avatars' },
    { to: '/integrations', label: 'integrations', hint: 'API · MCP' },
  ] },
];

// A tiny terminal-window mark: traffic lights + a `>_` prompt.
function Logo() {
  return (
    <div className="flex h-9 w-9 flex-col items-start justify-center gap-1 rounded-md border border-black/20 bg-zinc-900 px-1.5 shadow-sm">
      <div className="flex gap-[3px]">
        <span className="h-1 w-1 rounded-full bg-red-400" />
        <span className="h-1 w-1 rounded-full bg-amber-400" />
        <span className="h-1 w-1 rounded-full bg-emerald-400" />
      </div>
      <span className="text-[10px] font-bold leading-none text-primary">&gt;_</span>
    </div>
  );
}

function StatChip({ label, value, tone = 'default' }) {
  const tones = {
    default: 'text-foreground',
    danger: 'text-red-500',
    warn: 'text-amber-500',
  };
  return (
    <span className="hidden items-center gap-0.5 rounded border border-border bg-card px-2 py-1 text-xs sm:inline-flex">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-muted-foreground">:</span>
      <span className={`font-semibold ${tones[tone]}`}>{value}</span>
    </span>
  );
}

const AppShell = () => {
  const { user, logout } = useAuth();
  const { summary, scope, setScope, openNewTicket, modalOpen, members, myId } = useTickets();
  const location = useLocation();
  const navigate = useNavigate();
  const newCount = summary.new;
  const command = location.pathname.replace('/', '') || 'board';
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer only

  // Default to the dark terminal theme unless the user explicitly chose light.
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') !== 'light');

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Keyboard shortcuts: ⌘K/Ctrl+K = palette, n = new ticket (when not typing).
  useEffect(() => {
    const onKey = (e) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (cmd || e.altKey) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
      if (paletteOpen || modalOpen) return;
      if (e.key === 't' && TERMY_ENABLED) {
        e.preventDefault();
        setChatOpen((o) => !o);
        return;
      }
      if (e.key === 'n') {
        e.preventDefault();
        openNewTicket();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openNewTicket, paletteOpen, modalOpen]);

  const commands = [
    { id: 'board', label: 'Go to board', run: () => navigate('/board') },
    { id: 'planning', label: 'Go to planning', run: () => navigate('/planning') },
    { id: 'tickets', label: 'Go to all tickets', run: () => navigate('/tickets') },
    { id: 'integrations', label: 'Integrations (API tokens, MCP for Claude)', run: () => navigate('/integrations') },
    { id: 'team', label: 'Team & avatars', run: () => navigate('/team') },
    { id: 'new ticket', label: 'New ticket', hint: 'n', run: () => openNewTicket() },
    ...(TERMY_ENABLED ? [{ id: 'termy', label: 'Ask Termy (add a task)', hint: 't', run: () => setChatOpen(true) }] : []),
    { id: 'mine', label: 'Show my tickets', run: () => setScope('mine') },
    { id: 'team', label: 'Show team tickets', run: () => setScope('team') },
    { id: 'theme', label: 'Toggle light / dark', run: () => setDarkMode((p) => !p) },
  ];

  // Sidebar avatar: the member record (critter / upload, kept fresh by the
  // provider) with the Clerk photo as a fallback before initials.
  const me = members.find((m) => m.id === myId);
  const meForAvatar = {
    ...(me || {}),
    name: me?.name || user?.displayName,
    username: me?.username || user?.username,
    avatar_url: me?.avatar_url || user?.clerkImageUrl || user?.profile?.avatar,
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Mobile drawer backdrop (md+ never shows it) */}
      {sidebarOpen ? (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      ) : null}
      {/* Sidebar — off-canvas drawer on mobile, unchanged sticky column on desktop (md+) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-screen w-60 shrink-0 flex-col justify-between border-r border-border bg-card transition-transform',
          'md:sticky md:top-0 md:z-auto md:translate-x-0 md:bg-card/60 md:transition-none',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
      >
        <div className="space-y-6 px-3 py-5">
          <div className="flex items-center gap-2.5 px-1">
            <Logo />
            <div className="flex items-baseline text-lg font-semibold tracking-tight">
              <span className="text-muted-foreground">~/</span>
              <span>ticky</span>
            </div>
          </div>

          <nav className="space-y-4">
            {NAV_SECTIONS.map((section) => (
            <div key={section.title} className="space-y-0.5">
              <p className="mb-1 px-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground/50">
                {'// '}{section.title}
              </p>
            {section.items.map(({ to, label, hint }) => {
              const count = to === '/board' ? newCount : 0;
              return (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-accent text-foreground'
                        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={isActive ? 'text-primary' : 'text-muted-foreground/50'}>
                        {isActive ? '›' : '$'}
                      </span>
                      <span className="flex-1">{label}</span>
                      {count > 0 ? (
                        <span
                          className="rounded bg-primary px-1.5 text-xs font-semibold text-primary-foreground"
                          title={`${count} new`}
                        >
                          {count}
                        </span>
                      ) : null}
                      {hint ? <span className="text-[10px] text-muted-foreground/60">{hint}</span> : null}
                    </>
                  )}
                </NavLink>
              );
            })}
            </div>
            ))}
          </nav>
        </div>

        <div className="space-y-1 px-3 pb-5">
          {TERMY_ENABLED ? (
            <button
              type="button"
              onClick={() => setChatOpen(true)}
              className="group mb-1 flex w-full items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-2 text-sm shadow-sm transition hover:border-primary/40 hover:bg-accent"
              title="Ask Termy — add a task by chat (t)"
            >
              <img src={tickyMascot} alt="" className="ticky-float h-7 w-7 shrink-0" />
              <span className="flex-1 text-left font-medium text-foreground">ask termy</span>
              <kbd className="rounded border border-border px-1 text-[10px] text-muted-foreground">t</kbd>
            </button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground"
            onClick={() => setDarkMode((prev) => !prev)}
          >
            {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {darkMode ? 'light' : 'dark'}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                title={user?.displayName}
              >
                <UserAvatar user={meForAvatar} className="h-8 w-8 rounded-md text-xs" />
                <span className="text-foreground">{user?.displayName || 'user'}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4" />
                <span>{user?.displayName || 'user'}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/team')} className="gap-2">
                <Smile className="h-4 w-4" />
                change my avatar
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => logout()} className="gap-2">
                <LogOut className="h-4 w-4" />
                logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-background/80 px-6 py-3 backdrop-blur">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="-ml-1 rounded-md p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="flex items-center gap-2 text-sm font-normal">
              <span className="text-muted-foreground">ticky</span>
              <span className="text-primary">$</span>
              <span className="font-semibold text-foreground">{command}</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-md border border-border text-xs" title="Team = everyone's tickets · Mine = assigned to you">
              {['team', 'mine'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={
                    scope === s
                      ? 'bg-primary px-2.5 py-1 font-medium text-primary-foreground'
                      : 'px-2.5 py-1 text-muted-foreground transition hover:bg-accent'
                  }
                >
                  {s}
                </button>
              ))}
            </div>
            <NotificationBell />
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="hidden items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground transition hover:bg-accent md:inline-flex"
              title="Command palette (⌘K)"
            >
              ⌘K
            </button>
            <StatChip label="open" value={summary.open} />
            <StatChip label="this_week" value={summary.due_this_week} tone="warn" />
            {summary.overdue > 0 ? <StatChip label="overdue" value={summary.overdue} tone="danger" /> : null}
            <Button onClick={() => openNewTicket()} className="gap-1.5">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">new ticket</span>
              <kbd className="ml-0.5 hidden rounded border border-primary-foreground/30 px-1 text-[10px] font-normal sm:inline">n</kbd>
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>

        <StatusBar />
      </div>

      {/* Global create/edit modal + command palette */}
      <TicketModal />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} commands={commands} />
      <Celebration />
      {TERMY_ENABLED ? <TermyChat open={chatOpen} onClose={() => setChatOpen(false)} /> : null}
    </div>
  );
};

export default AppShell;
